import api from './api';

export interface QuizAssignment {
  id: number;
  student: number;
  student_name: string;
  quiz: number;
  quiz_question: string;
  quiz_reponse: string;
  quiz_chapitre: string;
  session: number | null;
  session_name: string | null;
  assigned_date: string;
  due_date: string | null;
  status: 'pending' | 'in_progress' | 'completed' | 'reviewed';
  student_answer: string;
  score: number | null;
  feedback: string;
  completed_date: string | null;
  created_by: number;
  created_by_name: string;
  student_question_score: number;
  times_asked: number;
}

export interface QuestionHistory {
  id: number;
  date: string;
  score: number | null;
  was_correct: boolean | null;
  student_answer: string;
  feedback: string;
}

class QuizAssignmentService {
  async getAll(params?: any): Promise<QuizAssignment[]> {
    const response = await api.get('/quiz-assignments/', { params });
    if (response.data && response.data.results) {
      return response.data.results;
    }
    return response.data;
  }

  async getByStudent(studentId: number): Promise<QuizAssignment[]> {
    const response = await api.get('/quiz-assignments/', {
      params: { student: studentId }
    });
    if (response.data && response.data.results) {
      return response.data.results;
    }
    return response.data;
  }

  async getAssignment(id: number): Promise<QuizAssignment> {
    const response = await api.get(`/quiz-assignments/${id}/`);
    return response.data;
  }

  async create(data: Partial<QuizAssignment>): Promise<QuizAssignment> {
    const response = await api.post('/quiz-assignments/', data);
    return response.data;
  }

  async update(id: number, data: Partial<QuizAssignment>): Promise<QuizAssignment> {
    const response = await api.patch(`/quiz-assignments/${id}/`, data);
    return response.data;
  }

  async delete(id: number): Promise<void> {
    await api.delete(`/quiz-assignments/${id}/`);
  }

  async submitAnswer(id: number, studentAnswer: string): Promise<QuizAssignment> {
    const response = await api.post(`/quiz-assignments/${id}/submit_answer/`, {
      student_answer: studentAnswer
    });
    return response.data;
  }

  async review(id: number, score: number, feedback: string): Promise<QuizAssignment> {
    const response = await api.post(`/quiz-assignments/${id}/review/`, {
      score,
      feedback
    });
    return response.data;
  }

  async bulkAssign(quizId: number, studentIds: number[], dueDate?: string): Promise<QuizAssignment[]> {
    const response = await api.post('/quiz-assignments/bulk_assign/', {
      quiz_id: quizId,
      student_ids: studentIds,
      due_date: dueDate
    });
    return response.data;
  }

  async getNextDue(studentId: number): Promise<QuizAssignment | null> {
    try {
      const response = await api.get('/quiz-assignments/next_due/', {
        params: { student: studentId }
      });
      return response.data;
    } catch (error: any) {
      if (error.response?.status === 404) {
        return null;
      }
      throw error;
    }
  }

  async getQuestionHistory(studentId: number, quizId: number): Promise<QuestionHistory[]> {
    const response = await api.get('/quiz-assignments/question_history/', {
      params: {
        student: studentId,
        quiz: quizId
      }
    });
    return response.data;
  }
}

const quizAssignmentService = new QuizAssignmentService();
export default quizAssignmentService;
